import { State, Selector, Action, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { GetMoreTeamsToDisplay } from 'ng2-app/store/performance/performance.action';
import { Injectable } from '@angular/core';
import { CXPerformanceService } from 'app/services/performance/cx-performance.service';
import { GetCXReports } from './cx-performance.action';
import { CXReport } from 'app/models/cx-report/cx-report.model';
import { concat } from 'lodash-es';

/**
 * replace null values in the object with 0
 *
 * @param obj
 *  */
const replacenull = (obj: object) => {
  const keys = Object.keys(obj);
  keys.forEach(item => {
    if (obj[item] === null) {
      obj[item] = 0;
    }
  });
  return obj;
};

const cxViewFilters = {
  MY_TEAM: 'My Team',
  ALL_TEAMS: 'All Teams',
  ALL_REPS: 'All Reps'
} as const;

export type CxViewFilter = typeof cxViewFilters[keyof typeof cxViewFilters];


export interface CXPerformanceStateModel {
  reports: CXReport[];
  addInfoReport: any[];
  repTeams: CXReport[];
  repsGroupedByTeam: any;
  loggedInTeamName: string;
  loading: boolean;
  otherTeams: string[];
  teamsToDisplay: any;
  lastRefreshDate: string;
}

@State<CXPerformanceStateModel>({
  name: 'cxperformance',
  defaults: {
    reports: [],
    addInfoReport: [],
    repTeams: [],
    repsGroupedByTeam: {},
    loggedInTeamName: '',
    loading: true,
    otherTeams: [],
    teamsToDisplay: {},
    lastRefreshDate: ' '
  }
})

@Injectable()

export class CXPerformanceState {


  @Selector()
  static loading(state: CXPerformanceStateModel) {
    return state?.loading ?? false;
  }


  @Selector()
  static loggedInTeamName(state: CXPerformanceStateModel) {
    return state?.loggedInTeamName ?? '';
  }

  /**
   * returns the perfomance data for the logged in rep
   *
   * @param state
   */
  @Selector()
  static loggedInRepTeam(state: CXPerformanceStateModel) {
    return state?.repTeams ?? [];
  }

  /**
   * returns all records for all reps
   *
   * @param state
   */
  @Selector()
  static reports(state: CXPerformanceStateModel) {
    return state?.reports ?? [];
  }

  /**
   * returns all records for all reps
   *
   * @param state
   */
  @Selector()
  static addInfoReport(state: CXPerformanceStateModel) {
    return state?.addInfoReport ?? [];
  }

  /**
   * returns the last date/time the cx report was refreshed
   *
   * @param state
   *
   */
  @Selector()
  static lastRefreshDate(state: CXPerformanceStateModel) {
    return state?.lastRefreshDate ?? '';
  }

  /**
   * returns all records for all reps grouped by team
   *
   * @param state
   */
  @Selector()
  static repsGroupedByTeam(state: CXPerformanceStateModel) {
    return state?.repsGroupedByTeam ?? null;
  }

  /**
   * returns a list of teams to for display, starting with the first 3 teams.
   *
   * @param state
   */
   @Selector()
   static teamsToDisplay(state: CXPerformanceStateModel) {
     return state?.teamsToDisplay ?? null;
   }

  /**
   * returns a list of teams to for display, starting with the first 3 teams.
   *
   * @param state
   */
  @Selector()
  static moreTeamsAvailable(state: CXPerformanceStateModel) {
    return state?.teamsToDisplay?.size < state?.otherTeams?.length;
  }

  constructor(
    private cxPerformanceService: CXPerformanceService,
  ) { }


  getGroupByTeams(reports: any, user: string) {
    const loggedInTeamName = reports.find(el => el.repName === user).team;
    const repTeams = reports.filter(el => el.team === loggedInTeamName);
    const repsGroupedByTeam = new Map<string, any>();
    reports.forEach((el: CXReport) => {
      if (repsGroupedByTeam.has(el.team)) {
        const arr = repsGroupedByTeam.get(el.team);
        repsGroupedByTeam.set(el.team, [...arr, el]);
      } else {
        repsGroupedByTeam.set(el.team, [el]);
      }
    });
    repsGroupedByTeam.delete(loggedInTeamName);
    const otherTeams = Array.from(repsGroupedByTeam.keys()).sort();
    const teamsToDisplay = this.getMoreTeams(new Map<string, any>(), otherTeams, repsGroupedByTeam, 3);
    return { loggedInTeamName, repTeams, repsGroupedByTeam, otherTeams, teamsToDisplay };
  }

  getMoreTeams(teamsToDisplay: Map<string, any>, otherTeams: string[], repsGroupedByTeam: Map<string, any>, numberTeams: number) {
    for (let i = 0; i < numberTeams; i++) {
      if (teamsToDisplay.size < otherTeams.length) {
        teamsToDisplay.set(otherTeams[teamsToDisplay.size], repsGroupedByTeam.get(otherTeams[teamsToDisplay.size]));
      }
    }
    return teamsToDisplay;
  }

  /**
   * This function will add more teams to the state
   *
   * @param ctx
   * @param param1
   */
  @Action(GetMoreTeamsToDisplay)
  getMoreTeamsToDisplay(
    ctx: StateContext<CXPerformanceStateModel>,
    { currentView, numberOfTeams }: GetMoreTeamsToDisplay) {
    const currState = ctx.getState();
    if (currState.teamsToDisplay.size < currState.otherTeams.length && currentView === cxViewFilters.ALL_TEAMS) {
        const teamsToDisplay = this.getMoreTeams(currState.teamsToDisplay,
                                                currState.otherTeams,
                                                currState.repsGroupedByTeam,
                                                numberOfTeams);
        setTimeout(() => {
          ctx.patchState({ teamsToDisplay: teamsToDisplay });
        }, 200);
      }
  }

  /**
   * if the filteredView is 'All Teams' then massage the data accordingly
   *
   * @param ctx
   * @param param1
   */
  @Action(GetCXReports)
  getRepReports(ctx: StateContext<CXPerformanceStateModel>, { currentView, user, autoRefresh }:
    GetCXReports) {
    const currState = ctx.getState();
    if (!autoRefresh) {
      ctx.patchState({ loading: true });
    }
    if (currState.reports.length === 0 || autoRefresh) { // data getting loaded first time
      return this.cxPerformanceService.getCXReports(autoRefresh).pipe(catchError(err => throwError(() => err)),
        map(data => {
          data.reduce((reports, otherData) => {
            const allReports = reports.map((el: CXReport) => replacenull(el));
            const repReport = { ...allReports.find(elem => elem.repName === user['repName']) };

            // any reps who're online and not actively paused
            const availableReps = allReports.filter(elem =>
              elem.isOnline
              && elem.pausedDuration === 0
            );
            // any reps who're online but are actively paused
            const pausedReps = allReports.filter(elem =>
              elem.isOnline
              && elem.pausedDuration > 0
            );
            // any reps who're offline and actively paused
            const lunchReps = allReports.filter(elem =>
              !elem.isOnline
              && elem.pausedDuration > 0
            );
            // any reps who're offline and not actively paused
            const oooReps = allReports.filter(elem =>
              !elem.isOnline
              && elem.pausedDuration === 0
            );
            // concat our newly arranged report
            const modifiedReps = concat(availableReps, pausedReps, lunchReps, oooReps);

            if (repReport && Object.keys(repReport).length > 0) {
              // from modified reports get the logged in user report and move to the top
              const idx = modifiedReps.findIndex(elem => elem.repName === user['repName']);
              modifiedReps.splice(idx, 1);
              modifiedReps.unshift(repReport);
            } else {
              const nonSalesReport: CXReport = {
                lenderUserId: user['lenderUserId'],
                repName: user['repName'],
                team: 'Non-sales Lendio',
                totalActivities: 0,
                activitiesPerHour: 0,
                textsSent: 0,
                emailsSent: 0,
                outgoingCalls: 0,
                averageCallTime: 0,
                cxTotalAnsweredDirectCalls: 0,
                cxTotalAnsweredQueueCalls: 0,
                cxTotalMissedQueueCalls: 0,
                cxTotalDispatchedQueueCalls: 0,
                totalCallTime: 0,
                idleTime: 0,
                isOnline: true,
                pausedDuration: 0,
                cxTotalPausedTime: 0
              };
              modifiedReps.unshift(nonSalesReport);
            }

            const {
              loggedInTeamName,
              repTeams,
              repsGroupedByTeam,
              otherTeams,
              teamsToDisplay
            } = this.getGroupByTeams(modifiedReps, user['repName']);

            if (currentView === cxViewFilters.MY_TEAM) {
              ctx.patchState({
                reports: modifiedReps,
                loading: false,
                loggedInTeamName,
                repTeams,
                lastRefreshDate: otherData['date'],
                addInfoReport: otherData['addInfoReport']
              });
            } else if (currentView === cxViewFilters.ALL_REPS) {
              ctx.patchState({
                reports: modifiedReps,
                loading: false,
                lastRefreshDate: otherData['date'],
                addInfoReport: otherData['addInfoReport']
              });
            } else if (currentView === cxViewFilters.ALL_TEAMS) {
              ctx.patchState({
                reports: modifiedReps,
                loading: false,
                loggedInTeamName,
                repTeams,
                repsGroupedByTeam,
                otherTeams,
                teamsToDisplay,
                lastRefreshDate: otherData['date'],
                addInfoReport: otherData['addInfoReport']
              });
            }
          });
        }));
    } else { // return existing reports
      if (currentView === cxViewFilters.MY_TEAM) {
        const { loggedInTeamName, repTeams, } = this.getGroupByTeams(currState.reports, user['repName']);
        setTimeout(() => {
          ctx.patchState({
            reports: currState.reports,
            loading: false,
            loggedInTeamName: loggedInTeamName,
            repTeams: repTeams,
            lastRefreshDate: currState.lastRefreshDate
          });
        }
          , 350);
      } else if (currentView === cxViewFilters.ALL_REPS) {
        setTimeout(() => {
          ctx.patchState({
            reports: currState.reports,
            loading: false,
            lastRefreshDate: currState.lastRefreshDate
          });
        }
          , 350);
      } else if (currentView === cxViewFilters.ALL_TEAMS) {
        const { loggedInTeamName, repTeams, repsGroupedByTeam, otherTeams, teamsToDisplay } =
        this.getGroupByTeams(currState.reports, user['repName']);
        setTimeout(() => {
          ctx.patchState({
            reports: currState.reports,
            loading: false,
            loggedInTeamName: loggedInTeamName,
            repTeams: repTeams,
            repsGroupedByTeam: repsGroupedByTeam,
            otherTeams: otherTeams,
            teamsToDisplay: teamsToDisplay,
            lastRefreshDate: currState.lastRefreshDate
          });
        }, 350);
      }
    }
  }
}
